import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { api, type MissingDataClient, type Employee } from '../../lib/api';
import { unwrapApi } from '../../lib/unwrapApi';

interface FixMissingDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: MissingDataClient;
  onFixed?: () => void;
}

// Field definitions for missing data
const FIELD_CONFIG: Record<string, {
  label: string;
  type: 'text' | 'number' | 'email' | 'tel' | 'select' | 'currency' | 'date';
  placeholder?: string;
  selectOptions?: string[];
  helpText?: string;
}> = {
  Tech: { label: 'Assigned Tech', type: 'select' },
  Address: { label: 'Address', type: 'text', placeholder: 'Street address' },
  City: { label: 'City', type: 'text', placeholder: 'City' },
  State: { label: 'State', type: 'text', placeholder: 'CA' },
  Zip: { label: 'Zip Code', type: 'text', placeholder: '94XXX' },
  'Stop Order': { label: 'Stop Order', type: 'number', placeholder: '1' },
  Area: { label: 'Area Code', type: 'text', placeholder: 'Area code' },
  Interval: { label: 'Service Interval (days)', type: 'number', placeholder: '14' },
  'Stop Duration': { label: 'Stop Duration (min)', type: 'number', placeholder: '30' },
  Services: { label: 'Services', type: 'text', placeholder: 'Maintenance, Watering' },
  'Monthly Amount': { label: 'Monthly Amount', type: 'currency', placeholder: '150.00' },
  'Contact Name': { label: 'Contact Name', type: 'text', placeholder: 'Contact name' },
  'Contact Email/Phone': { label: 'Contact Email or Phone', type: 'text', placeholder: 'email@example.com or (555) 123-4567' },
  'Start Date': { label: 'Client Start Date', type: 'date', helpText: 'When this client started service' },
};

export function FixMissingDataModal({
  isOpen,
  onClose,
  client,
  onFixed,
}: FixMissingDataModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Record<string, string>>({});

  // Fetch employees for tech dropdown
  const { data: employeesResponse } = useQuery({
    queryKey: ['employees', 'active'],
    queryFn: () => api.employees.list(true),
    enabled: isOpen && client.missingFields.includes('Tech'),
  });
  const employees = employeesResponse?.data || [];

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      const initial: Record<string, string> = {};
      client.missingFields.forEach(field => {
        initial[field] = '';
      });
      setFormData(initial);
    }
  }, [isOpen, client.missingFields]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      // Build the update payload from form data
      const updates: Record<string, unknown> = {};

      for (const [field, value] of Object.entries(formData)) {
        if (!value.trim()) continue;

        switch (field) {
          case 'Tech':
            updates.techId = value;
            break;
          case 'Address':
            updates.address = {
              ...(client.address || {}),
              address1: value,
            };
            break;
          case 'City':
            updates.address = {
              ...(client.address || {}),
              ...updates.address as Record<string, unknown>,
              city: value,
            };
            break;
          case 'State':
            updates.address = {
              ...(client.address || {}),
              ...updates.address as Record<string, unknown>,
              state: value,
            };
            break;
          case 'Zip':
            updates.address = {
              ...(client.address || {}),
              ...updates.address as Record<string, unknown>,
              zip: value,
            };
            break;
          case 'Stop Order':
            updates.stopOrder = parseInt(value, 10);
            break;
          case 'Area':
            updates.areaCode = value;
            break;
          case 'Interval':
            updates.intervalDays = parseInt(value, 10);
            break;
          case 'Stop Duration':
            updates.stopDuration = parseInt(value, 10);
            break;
          case 'Services':
            updates.services = value.split(',').map(s => s.trim()).filter(Boolean);
            break;
          case 'Monthly Amount':
            updates.monthlyAmount = value.replace(/[^0-9.]/g, '');
            break;
          case 'Contact Name':
            updates.contact = value;
            break;
          case 'Contact Email/Phone':
            // Determine if email or phone
            if (value.includes('@')) {
              updates.contactEmail = value;
            } else {
              updates.contactPhone = value;
            }
            break;
          case 'Start Date':
            updates.startDate = value; // Format: YYYY-MM-DD from date input
            break;
        }
      }

      if (Object.keys(updates).length === 0) {
        throw new Error('No fields to update');
      }

      return api.clients.update(client.id, updates).then(unwrapApi);
    },
    onSuccess: () => {
      // Invalidate dashboard queries to refresh counts
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      onFixed?.();
      handleClose();
    },
  });

  const handleClose = () => {
    setFormData({});
    onClose();
  };

  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const filledCount = Object.values(formData).filter(v => v.trim()).length;
  const totalCount = client.missingFields.length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div
        className="bg-white w-full sm:w-[560px] sm:rounded-2xl sm:max-h-[90vh] max-h-[95vh] overflow-hidden flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-forest-800 text-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-semibold">Fix Missing Data</h2>
              <p className="text-sm text-white/70 mt-0.5">{client.clientName}</p>
            </div>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Client Info */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-sage-600">Route</p>
              <p className="font-medium text-forest-900 font-mono">{client.routeId || 'No route'}</p>
            </div>
            <Badge variant={filledCount === totalCount ? 'success' : 'warning'}>
              {filledCount} / {totalCount} filled
            </Badge>
          </div>

          {/* Missing Fields Form */}
          <div className="space-y-4">
            <p className="text-sm font-medium text-sage-600">Missing Fields</p>
            {client.missingFields.map(field => {
              const config = FIELD_CONFIG[field] || { label: field, type: 'text' };

              return (
                <div key={field}>
                  <label className="block text-sm font-medium text-forest-700 mb-1.5">
                    {config.label}
                  </label>
                  {config.type === 'select' && field === 'Tech' ? (
                    <select
                      value={formData[field] || ''}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                      className="w-full px-3 py-2.5 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none bg-white"
                    >
                      <option value="">Select tech...</option>
                      {employees.map((emp: Employee) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name}
                        </option>
                      ))}
                    </select>
                  ) : config.type === 'date' ? (
                    <div>
                      <input
                        type="date"
                        value={formData[field] || ''}
                        onChange={(e) => handleFieldChange(field, e.target.value)}
                        className="w-full px-3 py-2.5 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none"
                      />
                      {config.helpText && (
                        <p className="text-xs text-sage-500 mt-1">{config.helpText}</p>
                      )}
                    </div>
                  ) : config.type === 'currency' ? (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sage-500">$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formData[field] || ''}
                        onChange={(e) => handleFieldChange(field, e.target.value)}
                        placeholder={config.placeholder}
                        className="w-full pl-7 pr-3 py-2.5 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none"
                      />
                    </div>
                  ) : (
                    <input
                      type={config.type === 'number' ? 'text' : config.type}
                      inputMode={config.type === 'number' ? 'numeric' : undefined}
                      value={formData[field] || ''}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                      placeholder={config.placeholder}
                      className="w-full px-3 py-2.5 rounded-xl border-2 border-sage-200 focus:border-primary-500 focus:outline-none"
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Error Display */}
          {updateMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-sm text-red-700">
                Failed to update client. Please try again.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-sage-200 px-6 py-4 flex gap-3">
          <Button variant="ghost" onClick={handleClose} className="flex-1">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => updateMutation.mutate()}
            disabled={filledCount === 0 || updateMutation.isPending}
            className="flex-1"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
